import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Plus, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "INR", "AED", "EUR", "GBP"];

const Costs = () => {
  const [items, setItems] = useState([]);
  const [heads, setHeads] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    head: "",
    amount: "",
    currency: "USD",
    entry_type: "monthly",
    period_month: new Date().toISOString().slice(0, 7),
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [itemsR, headsR, sumR] = await Promise.all([
        api.get("/costs"),
        api.get("/costs/heads"),
        api.get("/costs/summary"),
      ]);
      setItems(itemsR.data.items || []);
      setHeads(headsR.data.heads || []);
      setSummary(sumR.data.summary || []);
      if (!form.head && headsR.data.heads?.length) {
        setForm((f) => ({ ...f, head: headsR.data.heads[0] }));
      }
    } catch (e) {
      toast.error("Failed to load costs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.head || !form.amount) {
      toast.error("Head and amount are required");
      return;
    }
    try {
      const payload = {
        head: form.head,
        amount: parseFloat(form.amount),
        currency: form.currency,
        entry_type: form.entry_type,
        notes: form.notes || null,
      };
      if (form.entry_type === "monthly") payload.period_month = form.period_month;
      else payload.date = form.date;

      if (editing) {
        await api.put(`/costs/${editing.id}`, payload);
        toast.success("Cost entry updated");
      } else {
        await api.post("/costs", payload);
        toast.success("Cost entry added");
      }
      setShowForm(false);
      setEditing(null);
      setForm((f) => ({ ...f, amount: "", notes: "" }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save cost");
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      head: heads[0] || "",
      amount: "",
      currency: "USD",
      entry_type: "monthly",
      period_month: new Date().toISOString().slice(0, 7),
      date: new Date().toISOString().slice(0, 10),
      notes: "",
    });
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      head: c.head,
      amount: String(c.amount ?? ""),
      currency: c.currency || "USD",
      entry_type: c.entry_type || "monthly",
      period_month: c.period_month || new Date().toISOString().slice(0, 7),
      date: c.date || new Date().toISOString().slice(0, 10),
      notes: c.notes || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this cost entry?")) return;
    try {
      await api.delete(`/costs/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#666] mb-2">
            Operating Expenses · Multi-currency → USD
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter">Costs</h1>
          <div className="text-sm font-mono text-[#666] mt-2">
            India staff · UAE staff · Office · Partner commissions · MS Office · Travel · Visa
          </div>
        </div>
        <button
          onClick={openAdd}
          data-testid="add-cost-button"
          className="bg-[#1E3A8A] text-white px-4 py-2 font-medium tracking-wide text-sm hover:bg-[#172a63] transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Cost Entry
        </button>
      </div>

      {/* Summary cards by head */}
      <section>
        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#666] mb-3">
          / Spend by Head (USD)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {summary.map((s) => (
            <div
              key={s.head}
              data-testid={`cost-head-${s.head.replace(/\s+/g, "-").toLowerCase()}`}
              className="bg-white border border-[#E5E5E5] border-t-[3px] border-t-black p-4"
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2 leading-tight min-h-[24px]">
                {s.head}
              </div>
              <div className="text-2xl font-mono font-medium tracking-tight text-[#0A0A0A]">
                {fmtMoney(s.total_usd, "USD")}
              </div>
              <div className="text-xs font-mono text-[#666] mt-1">
                MTD: {fmtMoney(s.month_usd, "USD")}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <div
            className="bg-white border border-[#1E3A8A] w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="cost-form-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold tracking-tight">{editing ? "Edit" : "Add"} Cost Entry</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} data-testid="close-form">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Cost Head</label>
                <select
                  required
                  value={form.head}
                  onChange={(e) => setForm({ ...form, head: e.target.value })}
                  data-testid="form-head"
                  className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none"
                >
                  {heads.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Amount</label>
                  <input
                    required type="number" step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    data-testid="form-amount"
                    className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#0A0A0A] outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Currency</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    data-testid="form-currency"
                    className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none"
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Entry Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`border px-3 py-2 cursor-pointer text-sm font-medium ${form.entry_type === "monthly" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5]"}`}>
                    <input
                      type="radio" value="monthly" checked={form.entry_type === "monthly"}
                      onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
                      className="hidden"
                      data-testid="form-type-monthly"
                    />
                    Monthly Recurring
                  </label>
                  <label className={`border px-3 py-2 cursor-pointer text-sm font-medium ${form.entry_type === "one_time" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5]"}`}>
                    <input
                      type="radio" value="one_time" checked={form.entry_type === "one_time"}
                      onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
                      className="hidden"
                      data-testid="form-type-onetime"
                    />
                    One-time
                  </label>
                </div>
              </div>

              {form.entry_type === "monthly" ? (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Period (Month)</label>
                  <input
                    type="month" required
                    value={form.period_month}
                    onChange={(e) => setForm({ ...form, period_month: e.target.value })}
                    data-testid="form-period-month"
                    className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#0A0A0A] outline-none"
                  />
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Date</label>
                  <input
                    type="date" required
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="form-date"
                    className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-mono focus:border-[#0A0A0A] outline-none"
                  />
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-1.5 block">Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="form-notes"
                  rows={2}
                  className="w-full border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditing(null); }}
                  className="flex-1 border border-[#0A0A0A] px-4 py-2 text-sm font-medium hover:bg-[#F5F5F5]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="form-submit"
                  className="flex-1 bg-[#1E3A8A] text-white px-4 py-2 text-sm font-medium hover:bg-[#172a63]"
                >
                  {editing ? "Save Changes" : "Save Entry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="cost-entries-table">
        <table className="w-full border-collapse min-w-[800px]">
          <thead>
            <tr className="bg-[#F5F5F5] border-b border-[#0A0A0A]">
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Head</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Type</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Period / Date</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Amount</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">USD</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Notes</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-sm text-[#666] py-12">{loading ? "Loading..." : "No cost entries yet. Click + Add Cost Entry above."}</td></tr>
            ) : (
              items.map((c) => (
                <tr key={c.id} className="border-b border-[#E5E5E5] hover:bg-[#F9F9F9]" data-testid={`cost-row-${c.id}`}>
                  <td className="py-3 px-4 text-sm font-medium">{c.head}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c.entry_type === "monthly" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] border border-[#E5E5E5]"}`}>
                      {c.entry_type === "monthly" ? "Monthly" : "One-time"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-xs font-mono text-[#666]">{c.period_month || c.date || "—"}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(c.amount, c.currency, 2)}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right font-medium">{fmtMoney(c.amount_usd, "USD", 2)}</td>
                  <td className="py-3 px-4 text-xs text-[#666] max-w-[200px] truncate">{c.notes || "—"}</td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => openEdit(c)}
                      data-testid={`edit-cost-${c.id}`}
                      className="text-[#1E3A8A] hover:bg-[#F0F0F0] p-1.5 inline-flex mr-1"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      data-testid={`delete-cost-${c.id}`}
                      className="text-[#666] hover:text-[#DC2626] p-1.5 inline-flex"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Costs;
